import { createApi } from '@reduxjs/toolkit/query/react'
import { type BasePageRequest, type BasePageResponse, type BaseRequest, type BaseResponse } from '../models'
import {
  type BillingMetricsRecord,
  type CreditLot,
  type LedgerEntry,
  parseCoinAmount,
  type Wallet,
  type WalletConfig,
  type WalletType,
} from '../../models/Wallet'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _BASE_URL = import.meta.env.REACT_APP_ACCOUNTS_API_BASE_URL!

const normalizeWalletResponse = (response: BaseResponse<Wallet>): BaseResponse<Wallet> => ({
  ...response,
  data: {
    ...response.data,
    paidCoinBalance: parseCoinAmount(response.data.paidCoinBalance),
    bundledCoinBalance: parseCoinAmount(response.data.bundledCoinBalance),
    totalCoinBalance: parseCoinAmount(response.data.totalCoinBalance),
  },
})

const normalizeLedgerResponse = (response: BasePageResponse<LedgerEntry[]>): BasePageResponse<LedgerEntry[]> => ({
  ...response,
  data: response.data.map((entry) => ({ ...entry, amount: parseCoinAmount(entry.amount) })),
})

const normalizeLotsResponse = (response: BaseResponse<CreditLot[]>): BaseResponse<CreditLot[]> => ({
  ...response,
  data: response.data.map((lot) => ({ ...lot, amount: parseCoinAmount(lot.amount) })),
})

export interface GetBillingMetricsRecordRequest extends BaseRequest {
  billingMetricsRecordId: string
}

export interface GetWalletRequest extends BaseRequest {
  walletType: WalletType
}

export interface GetLedgerRequest extends BasePageRequest {
  walletType: WalletType
}

export interface GetLotsRequest extends BaseRequest {
  walletType: WalletType
}

export interface UpdateWalletConfigRequest extends BaseRequest {
  walletType: WalletType
  payload: {
    config: WalletConfig
  }
}

export const walletApi = createApi({
  reducerPath: 'walletApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Wallet', 'Ledger', 'Lots'],
  endpoints: (builder) => ({
    getWallet: builder.query<BaseResponse<Wallet>, GetWalletRequest>({
      query: (request) => `/coins/wallets/${request.accountId}/${request.walletType}`,
      transformResponse: normalizeWalletResponse,
      providesTags: ['Wallet'],
    }),
    updateWalletConfig: builder.mutation<BaseResponse<Wallet>, UpdateWalletConfigRequest>({
      query: (request) => ({
        url: `/coins/wallets/${request.accountId}/${request.walletType}`,
        method: 'PATCH',
        body: request.payload,
      }),
      transformResponse: normalizeWalletResponse,
      invalidatesTags: ['Wallet'],
    }),
    getLedger: builder.query<BasePageResponse<LedgerEntry[]>, GetLedgerRequest>({
      query: (request) => ({
        url: `/coins/wallets/${request.accountId}/${request.walletType}/ledger`,
        params: {
          page: request.pageRequest.page,
          size: request.pageRequest.size,
        },
      }),
      transformResponse: normalizeLedgerResponse,
      providesTags: ['Ledger'],
    }),
    getLots: builder.query<BaseResponse<CreditLot[]>, GetLotsRequest>({
      query: (request) => `/coins/wallets/${request.accountId}/${request.walletType}/lots`,
      transformResponse: normalizeLotsResponse,
      providesTags: ['Lots'],
    }),
    getBillingMetricsRecord: builder.query<BaseResponse<BillingMetricsRecord>, GetBillingMetricsRecordRequest>({
      query: (request) => `/accounts/${request.accountId}/billing-metrics/records/${request.billingMetricsRecordId}`,
    }),
  }),
})

export const { useGetWalletQuery, useGetLedgerQuery, useGetLotsQuery, useLazyGetBillingMetricsRecordQuery, useUpdateWalletConfigMutation } = walletApi
